import { Formik, Form } from 'formik'
import { pickAll, evolve, pipe } from 'ramda'
import { useEffect, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import { useDialogController } from '@machobear/design-system'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import ContentWrapper from '@/components/content-wrapper'
import { Row, Group, FieldInput, FieldDropdown, PhoneInput } from '@/components/form'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import Panel from '@/components/panel'
import AreaTitle from '@/components/area-title'
import useMediaQuery from '@/hooks/use-media-query'
import { select } from '@/store'
import { breakpoints } from '@/styles'
import { defaultIfFalsy } from '@/utils'
import { Box } from '@/components/structure'
import UserPicker from '@/components/user-picker'
import showUserLimitReached from './user-limit-reached'

const UserEdit = ({ userLimits, history, match }) => {
  const { t } = useTranslation()
  const { visible, showModal, closeModal } = useDialogController()

  const dispatchCompanyManagement = useDispatch().companyManagement
  const createUser = dispatchCompanyManagement.createUser
  const updateUser = dispatchCompanyManagement.updateUser
  const deleteTargetUser = dispatchCompanyManagement.deleteTargetUser

  const userId = match?.params?.userId
  const users = useSelector(state => state?.companyManagement?.users)
  const user = users?.[userId]

  const allLandlords = useSelector(select.companyManagement.users)

  const colSize = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`) ? 12 : 6

  useEffect(() => {
    if (!userLimits || userLimits.allowed) return
    history.replace('/manage/users')
  }, [history, userLimits])

  if (!user && userId) return null

  const handleBack = () => history.replace(userId ? `/manage/users/${userId}` : `/manage/users/`)

  const handleReassign = async selectedUser => {
    const response = await deleteTargetUser({
      oldUserId: userId,
      newUserId: selectedUser[0]?.id
    })

    if (response?.response?.ok) {
      history.replace(`/manage/users/`)
    }
  }

  const getPageTitle = () => {
    if (userId && user) {
      const nameEndsWithS = user.first_name.match(/.*(s)$/i)
      return `${user.first_name}${nameEndsWithS ? `'` : `'s`} ${t(
        'l.company_management.edit.profile.title',
        'Profile'
      )}`
    }
    return t('l.company_management.edit.create_new_user.title', 'Create New User')
  }

  const initialValues = pipe(
    pickAll(['first_name', 'last_name', 'preferred_name', 'phone_number', 'username', 'user_role']),
    evolve({
      first_name: defaultIfFalsy(''),
      last_name: defaultIfFalsy(''),
      preferred_name: defaultIfFalsy(''),
      phone_number: defaultIfFalsy(''),
      username: defaultIfFalsy(''),
      user_role: defaultIfFalsy('')
    })
  )(user || {})

  const formOptions = () => ({
    validationSchema: Yup.object().shape({
      first_name: Yup.string()
        .matches(
          /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
          t('b.profile.name.special_characters.error', 'Cannot include special characters')
        )
        .max(255)
        .required(),
      last_name: Yup.string()
        .matches(
          /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
          t('b.profile.name.special_characters.error', 'Cannot include special characters')
        )
        .max(255)
        .required(),
      preferred_name: Yup.string()
        .matches(
          /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
          t('b.profile.name.special_characters.error', 'Cannot include special characters')
        )
        .max(255),
      phone_number: Yup.string().phone().required(),
      username: Yup.string().email().trim().required(),
      user_role: Yup.string().required()
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const action = userId ? updateUser : createUser
      const response = await action({ userId, values })
      setSubmitting(false)
      if (response?.response?.ok) {
        handleBack()
      } else if (response?.error?.limit) {
        showUserLimitReached({})
      }
    }
  })

  return (
    <>
      <Page>
        <PageTitle>{getPageTitle()}</PageTitle>
        <AreaTitle>{getPageTitle()}</AreaTitle>
        <Formik {...formOptions()} initialValues={initialValues}>
          {({ values }) => (
            <Form className="flex-full">
              <ContentWrapper className="mb-50">
                <Panel>
                  <Row>
                    <Group col={colSize}>
                      <FieldInput
                        name="first_name"
                        placeholder={t(
                          'l.company_management.edit.legal_first_and_middle_name.label',
                          'Legal first & middle name'
                        )}
                        required
                      />
                    </Group>
                    <Group col={colSize}>
                      <FieldInput
                        name="last_name"
                        placeholder={t(
                          'l.company_management.edit.legal_last_name.label',
                          'Legal last name'
                        )}
                        required
                      />
                    </Group>
                  </Row>
                  <Row>
                    <Group col={colSize}>
                      <FieldInput
                        name="preferred_name"
                        placeholder={t(
                          'l.company_management.edit.preferred_name.label',
                          'Preferred name'
                        )}
                      />
                    </Group>
                    <Group col={colSize}>
                      <PhoneInput
                        value={values.phone_number}
                        name="phone_number"
                        disabled={!!userId}
                        countryCodeAlpha={user?.phone_country_code_alpha}
                        placeholder={t('r.profile.edit.phone_number.label', 'Phone number')}
                        required
                      />
                    </Group>
                  </Row>
                  <Row>
                    <Group col={colSize}>
                      <FieldInput
                        name="username"
                        placeholder={t('r.profile.edit.email.label', 'Email')}
                        required
                      />
                    </Group>
                    <Group col={colSize}>
                      <FieldDropdown
                        name="user_role"
                        placeholder={t('l.company_management.edit.role.label', 'Role')}
                        required
                        options={[
                          {
                            value: 'administrator',
                            label: t(
                              'l.company_management.edit.user_role_admin.option',
                              'Administrator'
                            )
                          },
                          {
                            value: 'agent',
                            label: t('l.company_management.edit.user_role_agent.option', 'Agent')
                          }
                        ]}
                      />
                    </Group>
                  </Row>
                </Panel>
              </ContentWrapper>
              <DefaultButtonBar handleBack={handleBack}>
                {!!userId && (
                  <Box left={8}>
                    <Button theme="delete" size="small" onClick={() => showModal()}>
                      {t(
                        'l.company_management.view.user_profile_edit_delete_user.button',
                        'Delete User'
                      )}
                    </Button>
                  </Box>
                )}
                <Box left="auto">
                  <Button theme="primary" size="small" type="submit">
                    {userId ? t('common.update', 'Update') : t('common.save', 'Save')}
                  </Button>
                </Box>
              </DefaultButtonBar>
            </Form>
          )}
        </Formik>
      </Page>
      <UserPicker
        visible={visible}
        onClose={closeModal}
        title={t(
          'l.company_management.edit.select_landlords_modal.title',
          'Select Primary Landlord'
        )}
        maxSelect={1}
        allUsers={allLandlords}
        activeUsers={[]}
        setActiveUsers={handleReassign}
        disabledUsers={[user]}
        buttonLabel={t(
          'l.company_management.edit.select_landlords_modal_reassign.button',
          'Re-assign'
        )}
        showLandlordAlerts
        alertTrackingId="app_upgrade_reassign_landlord_anchortext"
      />
    </>
  )
}

export default memo(UserEdit)
